import { DiscoveryApi, FetchApi, IdentityApi } from '@backstage/core-plugin-api';
import { VaultSecretsApi, VaultSecret } from './VaultSecretsApi';

export class VaultError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
    public readonly statusCode?: number,
  ) {
    super(message);
    this.name = 'VaultError';
  }
}

/**
 * Options for creating a VaultSecretsClient
 */
export interface VaultSecretsClientOptions {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
  identityApi: IdentityApi;
}

/**
 * Default implementation of the VaultSecretsApi
 */
export class VaultSecretsClient implements VaultSecretsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly identityApi: IdentityApi;

  constructor(options: VaultSecretsClientOptions) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.identityApi = options.identityApi;
  }

  private async getBaseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('vault-secrets');
  }

  private async getHeaders(): Promise<HeadersInit> {
    const { token } = await this.identityApi.getCredentials();
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  async getUserInfo(): Promise<{
    user: string;
    userEntityRef: string;
    vaultAuthenticated: boolean;
  }> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(`${baseUrl}/user-info`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new VaultError(
        errorData.error || `Failed to get user info: ${response.statusText}`,
        errorData.code,
        response.status
      );
    }

    return await response.json();
  }

  async listSecrets(path: string): Promise<string[]> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/secrets?path=${encodeURIComponent(path)}`,
      {
        method: 'GET',
        headers,
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new VaultError(
        errorData.error || `Failed to list secrets: ${response.statusText}`,
        errorData.code,
        response.status
      );
    }

    const data = await response.json();
    return data.secrets || [];
  }

  async readSecret(path: string): Promise<VaultSecret> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/secrets/${encodeURIComponent(path)}`,
      {
        method: 'GET',
        headers,
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new VaultError(
        errorData.error || `Failed to read secret: ${response.statusText}`,
        errorData.code,
        response.status
      );
    }

    return await response.json();
  }

  async writeSecret(path: string, data: Record<string, any>): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/secrets/${encodeURIComponent(path)}`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ data }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new VaultError(
        errorData.error || `Failed to write secret: ${response.statusText}`,
        errorData.code,
        response.status
      );
    }
  }

  async deleteSecret(path: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getHeaders();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/secrets/${encodeURIComponent(path)}`,
      {
        method: 'DELETE',
        headers,
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new VaultError(
        errorData.error || `Failed to delete secret: ${response.statusText}`,
        errorData.code,
        response.status
      );
    }
  }
}